import {
	HttpClient,
	HttpResponse
}                                    from '@angular/common/http';
import { Injectable }                from '@angular/core';
import { ArchivingKeyword }          from '@app/modules/keyword/models/archiving-keyword.model';
import { BusinessPremisesByKeyword } from '@app/modules/keyword/models/common-keyword.model';
import { LoggerService }             from '@app/shared/services/logger.service';
import { AbstractApiService }        from '@core/http/abstract-api.service';
import { SanitizerService }          from '@core/http/sanitizer.service';
import { TransitionService }         from '@core/http/transition.service';
import {
	IArchivingKeywordHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState
}                                    from '@core/interfaces/api.interface';
import { IList }                     from '@core/interfaces/list-response.interfaces';
import { ModelTypeEnum }             from '@models/transition.model';
import {
	BusinessPremisesApiClient,
	BusinessPremisesDTO,
	ClsCreateKeywordRequestDTO,
	ClsUpdateKeywordRequestDTO,
	CreateKeywordRequestDTO,
	CreateKeywordRequestParams,
	DeleteKeywordRequestParams,
	GetBusinessPremisesByKeywordRequestParams,
	GetKeywordRequestParams,
	GetKeywordsRequestParams,
	KeywordDTO,
	KeywordsApiClient,
	UpdateKeywordRequestDTO,
	UpdateKeywordRequestParams
}                                    from '@openApiGenerated/archiving-api';
import { Observable }                from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class ArchivingKeywordApiService extends AbstractApiService {

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly businessPremisesApiClient: BusinessPremisesApiClient,
		private readonly keywordsApiClient: KeywordsApiClient,
		private readonly sanitize: SanitizerService,
		private readonly transition: TransitionService
	) {
		super();
		this.http = http;
	}


	// SANITIZER ######################################
	private sanitizeApiResponse( entity: ArchivingKeyword, context = this ): void {
		if ( entity instanceof ArchivingKeyword ) {
			entity.created = context.sanitize.date<Date>( entity.created, ModelTypeEnum.TMO, Date );
			entity.lastModified = context.sanitize.date<Date>( entity.lastModified, ModelTypeEnum.TMO, Date );
		}
	}

	private sanitizeForApiRequest( dto: UpdateKeywordRequestDTO, context = this ): void {
		// add if needed
		// if ( dto instanceof ClsUpdateKeywordRequestDTO ) {
		//
		// }
	}

	// SPECIFIC MAPPER ################################

	// LIST TYPE REQUESTS #############################
	public search( params?: IArchivingKeywordHttpParams, throwError = false ): Observable<RequestCompleteListState<ArchivingKeyword>> {
		this.logger.debug( 'pre-list call' );

		const requestParam: GetKeywordsRequestParams = {
			keywordIds: params?.keywordIds,
			name: this.arrayUtils.checkForArrayArgAndReturnAsSingleValue<string>( params?.name ),
			pageSize: params?.paging?.pageSize,
			pageNumber: params?.paging?.pageNumber,
			sort: params?.sort
		};

		const req$ = this.keywordsApiClient.getKeywords( requestParam );

		return this.transition.handleListRequestResponse<KeywordDTO, ArchivingKeyword>(
			req$ as Observable<IList<KeywordDTO>>,
			ArchivingKeyword,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public businessPremisesIdsByKeywordId( keywordId: string ): Observable<RequestCompleteListState<BusinessPremisesByKeyword>> {

		const requestParam: GetBusinessPremisesByKeywordRequestParams = {
			keywordId
		};

		const req$ = this.businessPremisesApiClient.getBusinessPremisesByKeyword( requestParam );

		return this.transition.handleListRequestResponse<BusinessPremisesDTO, BusinessPremisesByKeyword>(
			req$ as Observable<IList<BusinessPremisesDTO>>,
			BusinessPremisesByKeyword,
			this
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public read( id: string, withFullResponse: boolean = true, throwError = false ): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		this.logger.debug( 'pre-read call: ', id );
		const requestParam: GetKeywordRequestParams = { keywordId: id };

		const req$ = this.keywordsApiClient.getKeyword( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<KeywordDTO | HttpResponse<KeywordDTO>, ArchivingKeyword>(
			req$,
			RequestActionEnum.READ,
			ArchivingKeyword,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public create(
		archivingKeyword: ArchivingKeyword,
		withFullResponse: boolean = true,
		throwError = false
	): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		this.logger.debug( 'pre-create call: ', { ...archivingKeyword } );

		const createKeywordRequestDTO = this.transition.prepareEntityRequest<ArchivingKeyword, CreateKeywordRequestDTO>(
			archivingKeyword, ClsCreateKeywordRequestDTO, this, this.sanitizeForApiRequest
		);

		const requestParam: CreateKeywordRequestParams = {
			createKeywordRequestDTO
		};

		const req$ = this.keywordsApiClient.createKeyword( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<KeywordDTO | HttpResponse<KeywordDTO>, ArchivingKeyword>(
			req$,
			RequestActionEnum.CREATE,
			ArchivingKeyword,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public update(
		archivingKeyword: ArchivingKeyword,
		withFullResponse: boolean = true,
		throwError = false
	): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		this.logger.debug( 'pre-update call: ', archivingKeyword?.id );


		const updateKeywordRequestDTO = this.transition.prepareEntityRequest<ArchivingKeyword, UpdateKeywordRequestDTO>(
			archivingKeyword, ClsUpdateKeywordRequestDTO, this, this.sanitizeForApiRequest
		);

		const requestParam: UpdateKeywordRequestParams = {
			keywordId: archivingKeyword.id,
			updateKeywordRequestDTO
		};

		const req$ = this.keywordsApiClient.updateKeyword( requestParam, withFullResponse ? 'response' : undefined );
		return this.transition.handleEntityRequestResponse<KeywordDTO | HttpResponse<KeywordDTO>, ArchivingKeyword>(
			req$,
			RequestActionEnum.UPDATE,
			ArchivingKeyword,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public delete( keywordId: string, throwError = false ): Observable<RequestCompleteEntityState<ArchivingKeyword>> {
		this.logger.debug( 'pre-delete call: ', keywordId );
		const requestParam: DeleteKeywordRequestParams = {
			keywordId
		};

		const req$ = this.keywordsApiClient.deleteKeyword( requestParam, 'response' );
		return this.transition.handleEntityRequestResponse<HttpResponse<any>, ArchivingKeyword>(
			req$,
			RequestActionEnum.DELETE,
			ArchivingKeyword,
			this,
			this.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}
}
